import { useCallback } from "react";

import { EXPEDITED_PROGRAM } from "@FillInTableContainer/shared/constants/expeditedProgramKey.constants";
import {
  DEFAULT_FOCUS_STATUS,
  useTableCreatorFocusStatus,
} from "@FillInTableContainer/shared/context/TableCreatorFocusStatusContext";
import { useTableCreatorCurrentHistory } from "@FillInTableContainer/shared/context/TableCreatorHistoryContext";
import {
  useTableCreatorPositionInfo,
  DEFAULT_POSITION_INFO,
} from "@FillInTableContainer/shared/context/TableCreatorPositionInfoContext";
import {
  useValidatedInTableCreator,
  DEFAULT_VALIDATED_INFO,
} from "@FillInTableContainer/shared/context/ValidatedInTableCreatorContext";
import { useCheckDisabledDelete } from "@FillInTableContainer/shared/hooks/useCheckDisabledDelete";
import { useEntryDataState } from "@stores/entryData";
import { getPressIdFromURL } from "@utils/getStateFromURL";

import { TableInfoProps } from "../ExpeditedProgramDrugProfileTable";

import type { ArticleEntryData } from "@/types/articleEntryData.types";

interface UseHandleTableCreatorTableReturn {
  handleClickDrugProfileDeleteButton: () => void;
  handleKeyDownToDeleteDrugProfileTable: (event: KeyboardEvent) => void;
}

export const useExpeditedProgramDrugProfileTable = (
  tablePropsInfo: TableInfoProps,
): UseHandleTableCreatorTableReturn => {
  const { groupTableKey, tableKey, groupTableIndex, tableIndex } =
    tablePropsInfo;

  const { changeCurrentHistory } = useTableCreatorCurrentHistory();
  const articleSection = EXPEDITED_PROGRAM;

  const { info, changeInfo } = useTableCreatorPositionInfo();
  const [entryData, setEntryData] = useEntryDataState(getPressIdFromURL());
  const { focusStatus, changeFocusStatus } = useTableCreatorFocusStatus();
  const { changeValidatedInfo } = useValidatedInTableCreator();
  const { checkDisabledDeleteTable } = useCheckDisabledDelete();

  const drugProfileTableLength =
    entryData[articleSection]?.["Drug Profile"]?.length || 0;

  const handleClearTable = useCallback(() => {
    const clearDrugProfile = (): ArticleEntryData => {
      return {
        ...entryData,
        [EXPEDITED_PROGRAM]: {
          ...entryData[EXPEDITED_PROGRAM],
          "Drug Profile": entryData[EXPEDITED_PROGRAM]?.["Drug Profile"]?.map(
            (tableInfo, tableInfoIndex) => {
              if (tableInfoIndex === info.tableIndex) {
                return {
                  ...tableInfo,
                  "Brand Name": {
                    ...tableInfo?.["Brand Name"],
                    value: [],
                  },
                  "Code Name": {
                    ...tableInfo?.["Code Name"],
                    value: [],
                  },
                  "INN Name": {
                    ...tableInfo?.["INN Name"],
                    value: [],
                  },
                  MOA: {
                    ...tableInfo?.MOA,
                    value: [],
                  },
                  "Therapeutic Area": {
                    ...tableInfo?.["Therapeutic Area"],
                    value: [],
                  },
                  Modality: {
                    ...tableInfo?.Modality,
                    value: [],
                  },
                  "Other Characteristics": {
                    ...tableInfo?.["Other Characteristics"],
                    value: [],
                  },
                  ROA: {
                    ...tableInfo?.ROA,
                    value: [],
                  },
                  Target: {
                    ...tableInfo?.Target,
                    value: [],
                  },
                  "Target Indication": {
                    ...tableInfo?.["Target Indication"],
                    value: [],
                  },
                  Phase: {
                    ...tableInfo?.Phase,
                    value: [],
                  },
                  "Originator/Developer": {
                    ...tableInfo?.["Originator/Developer"],
                    value: [],
                  },
                  "Generic / Biosimilar": {
                    ...tableInfo?.["Generic / Biosimilar"],
                    value: ["false"],
                  },
                  "Ref.Product Name": {
                    ...tableInfo?.["Ref.Product Name"],
                    value: [],
                  },
                };
              }
              return tableInfo;
            },
          ),
        },
      };
    };

    if (info.groupTableKey === "Drug Profile") {
      setEntryData(clearDrugProfile());
      changeCurrentHistory({
        entryData: clearDrugProfile(),
        changedDataInfo: {
          target: "table",
          action: "clear",
          position: {
            groupTableKey,
            groupTableIndex,
            tableKey,
            tableIndex,
            groupShellKey: null,
            groupShellIndex: null,
            shellKey: null,
            shellIndex: null,
            valueIndex: null,
            value: null,
          },
        },
      });

      if (drugProfileTableLength > 1 && !checkDisabledDeleteTable()) {
        changeFocusStatus({ type: "delete", container: "table" });
      } else {
        changeFocusStatus(DEFAULT_FOCUS_STATUS);
      }
    }
  }, [
    changeCurrentHistory,
    changeFocusStatus,
    checkDisabledDeleteTable,
    drugProfileTableLength,
    entryData,
    groupTableIndex,
    groupTableKey,
    info.groupTableKey,
    info.tableIndex,
    setEntryData,
    tableIndex,
    tableKey,
  ]);

  const handleDeleteTable = useCallback(() => {
    if (checkDisabledDeleteTable()) {
      return;
    }
    if (drugProfileTableLength > 1) {
      changeFocusStatus(DEFAULT_FOCUS_STATUS);

      const deleteDrugProfile = (): ArticleEntryData => {
        return {
          ...entryData,
          [EXPEDITED_PROGRAM]: {
            ...entryData[EXPEDITED_PROGRAM],
            "Drug Profile": entryData[EXPEDITED_PROGRAM]?.[
              "Drug Profile"
            ]?.filter(
              (tableInfo, tableInfoIndex) => tableInfoIndex !== info.tableIndex,
            ),
          },
        };
      };
      if (info.groupTableKey === "Drug Profile") {
        setEntryData(deleteDrugProfile());
        changeCurrentHistory({
          entryData: deleteDrugProfile(),
          changedDataInfo: {
            target: "table",
            action: "delete",
            position: {
              groupTableKey,
              groupTableIndex,
              tableKey,
              tableIndex,
              groupShellKey: null,
              groupShellIndex: null,
              shellKey: null,
              shellIndex: null,
              valueIndex: null,
              value: null,
            },
          },
        });
        changeInfo(DEFAULT_POSITION_INFO);
      }
    }
  }, [
    drugProfileTableLength,
    entryData,
    groupTableIndex,
    groupTableKey,
    info,
    tableIndex,
    tableKey,
    setEntryData,
    changeCurrentHistory,
    changeFocusStatus,
    changeInfo,
    checkDisabledDeleteTable,
  ]);

  const handleClickDrugProfileDeleteButton = () => {
    if (focusStatus.type === "clear") handleClearTable();
    if (focusStatus.type === "delete") handleDeleteTable();
  };

  const handleKeyDownToDeleteDrugProfileTable = useCallback(
    (event: KeyboardEvent) => {
      if (focusStatus.type === "clear" && event.key === "Delete") {
        handleClearTable();
      }
      if (focusStatus.type === "delete" && event.key === "Delete") {
        handleDeleteTable();
      }
      changeValidatedInfo(DEFAULT_VALIDATED_INFO);
    },
    [
      focusStatus.type,
      changeValidatedInfo,
      handleClearTable,
      handleDeleteTable,
    ],
  );

  return {
    handleClickDrugProfileDeleteButton,
    handleKeyDownToDeleteDrugProfileTable,
  };
};
